import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConfigurationPanel = ({ config, onConfigChange }) => {
  const { t } = useTranslation();

  const themeOptions = [
    { value: 'light', label: t('embedSettings.config.themeLight') },
    { value: 'dark', label: t('embedSettings.config.themeDark') },
    { value: 'auto', label: t('embedSettings.config.themeAuto') }
  ];

  const languageOptions = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' }
  ];

  const viewerModeOptions = [
    { value: 'flip', label: t('embedSettings.config.viewerModeFlip') },
    { value: 'slider', label: t('embedSettings.config.viewerModeSlider') }
  ];

  const presetTemplates = [
    { value: 'product', label: t('embedSettings.config.templateProduct') },
    { value: 'marketing', label: t('embedSettings.config.templateMarketing') },
    { value: 'technical', label: t('embedSettings.config.templateTechnical') },
    { value: 'custom', label: t('embedSettings.config.templateCustom') }
  ];

  const handlePresetChange = (preset) => {
    const presetConfigs = {
      product: {
        width: '100%',
        height: '800',
        responsive: true,
        initialPage: 1,
        theme: 'light',
        showNavigation: true,
        showSharing: true,
        showZoom: true,
        showFullscreen: false
      },
      marketing: {
        width: '100%',
        height: '600',
        responsive: true,
        initialPage: 1,
        theme: 'auto',
        showNavigation: true,
        showSharing: true,
        showZoom: false,
        showFullscreen: false
      },
      technical: {
        width: '1200',
        height: '900',
        responsive: false,
        initialPage: 1,
        theme: 'light',
        showNavigation: true,
        showSharing: false,
        showZoom: true,
        showFullscreen: false
      }
    };

    if (preset !== 'custom' && presetConfigs?.[preset]) {
      onConfigChange({ ...config, ...presetConfigs?.[preset], preset });
    } else {
      onConfigChange({ ...config, preset });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
      <div className="flex items-center mb-4 md:mb-6">
        <Icon name="Settings" size={20} className="text-accent mr-2 md:mr-3" />
        <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          {t('embedSettings.config.title')}
        </h2>
      </div>
      <div className="space-y-4 md:space-y-6">
        <Select
          label={t('embedSettings.config.templateLabel')}
          description={t('embedSettings.config.templateDesc')}
          options={presetTemplates}
          value={config?.preset || 'custom'}
          onChange={handlePresetChange}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label={t('embedSettings.config.width')}
            type="text"
            placeholder="100% or 800px"
            value={config?.width}
            onChange={(e) => onConfigChange({ ...config, width: e?.target?.value })}
            description={t('embedSettings.config.widthDesc')}
          />

          <Input
            label={t('embedSettings.config.height')}
            type="text"
            placeholder="600px"
            value={config?.height}
            onChange={(e) => onConfigChange({ ...config, height: e?.target?.value })}
            description={t('embedSettings.config.heightDesc')}
          />
        </div>

        <Checkbox
          label={t('embedSettings.config.responsive')}
          description={t('embedSettings.config.responsiveDesc')}
          checked={config?.responsive}
          onChange={(e) => onConfigChange({ ...config, responsive: e?.target?.checked })}
        />

        <Input
          label={t('embedSettings.config.initialPage')}
          type="number"
          min="1"
          placeholder="1"
          value={config?.initialPage}
          onChange={(e) => onConfigChange({ ...config, initialPage: parseInt(e?.target?.value) || 1 })}
          description={t('embedSettings.config.initialPageDesc')}
        />

        <Select
          label={t('embedSettings.config.theme')}
          description={t('embedSettings.config.themeDesc')}
          options={themeOptions}
          value={config?.theme}
          onChange={(theme) => onConfigChange({ ...config, theme })}
        />

        <Select
          label={t('embedSettings.config.language')}
          description={t('embedSettings.config.languageDesc')}
          options={languageOptions}
          value={config?.defaultLanguage || 'es'}
          onChange={(defaultLanguage) => onConfigChange({ ...config, defaultLanguage })}
        />

        <Select
          label={t('embedSettings.config.viewerMode')}
          description={t('embedSettings.config.viewerModeDesc')}
          options={viewerModeOptions}
          value={config?.viewerMode || 'flip'}
          onChange={(viewerMode) => onConfigChange({ ...config, viewerMode })}
        />

        <div className="border-t border-border pt-4 md:pt-6">
          <h3 className="text-base md:text-lg font-medium text-foreground mb-3 md:mb-4">
            {t('embedSettings.config.displayOptions')}
          </h3>
          <div className="space-y-3 md:space-y-4">
            <Checkbox
              label={t('embedSettings.config.showNavigation')}
              description={t('embedSettings.config.showNavigationDesc')}
              checked={config?.showNavigation}
              onChange={(e) => onConfigChange({ ...config, showNavigation: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showSharing')}
              description={t('embedSettings.config.showSharingDesc')}
              checked={config?.showSharing}
              onChange={(e) => onConfigChange({ ...config, showSharing: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showZoom')}
              description={t('embedSettings.config.showZoomDesc')}
              checked={config?.showZoom}
              onChange={(e) => onConfigChange({ ...config, showZoom: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showFullscreen')}
              description={t('embedSettings.config.showFullscreenDesc')}
              checked={config?.showFullscreen}
              onChange={(e) => onConfigChange({ ...config, showFullscreen: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showDownload')}
              description={t('embedSettings.config.showDownloadDesc')}
              checked={config?.showDownload}
              onChange={(e) => onConfigChange({ ...config, showDownload: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showMenu')}
              description={t('embedSettings.config.showMenuDesc')}
              checked={config?.showMenu}
              onChange={(e) => onConfigChange({ ...config, showMenu: e?.target?.checked })}
            />
            <Checkbox
              label={t('embedSettings.config.showSearch')}
              description={t('embedSettings.config.showSearchDesc')}
              checked={config?.showSearch}
              onChange={(e) => onConfigChange({ ...config, showSearch: e?.target?.checked })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;
